/**
 * Admin "Stakeholder report" panel — surfaces the weekly board-style PDF
 * roll-up of lead/engagement activity. Two controls: run an on-demand report
 * right now, and view the history of past runs (recipient, totals, delta,
 * per-collection counts, top sub-brands, Resend delivery id).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Send, RefreshCw, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso || "—"; }
};

const DeltaPill = ({ pct, lastWeek }) => {
  if (pct === null || pct === undefined) {
    if (lastWeek === 0) {
      return <span className="text-emerald-700 text-[11px] font-semibold">● First-week data</span>;
    }
    return <span className="text-zinc-400 text-[11px]"><Minus size={10} className="inline"/> —</span>;
  }
  const Up = pct >= 0;
  return (
    <span className={`text-[11px] font-semibold ${Up ? "text-emerald-700" : "text-red-700"}`}>
      {Up ? <TrendingUp size={11} className="inline mr-0.5"/> : <TrendingDown size={11} className="inline mr-0.5"/>}
      {Up ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
};

export default function StakeholderReportPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stakeholder-report/history");
      setItems(data.items || []);
    } catch { toast.error("Failed to load report history"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/stakeholder-report/run");
      if (data.sent) {
        toast.success(`Report sent · ${data.total_this_week} leads this week · ${data.recipient}`);
      } else {
        toast.warning(data.reason || "Report built but email skipped (no recipient).");
      }
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Report failed");
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="admin-stakeholder-report-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Weekly stakeholder report</h2>
          <p className="text-zinc-500 text-[13px] mt-1">
            One-page board-style PDF that rolls up last week's lead and engagement activity. Runs automatically every Monday at 06:00 UTC and is emailed via Resend to <code className="text-[12px] text-zinc-700">STAKEHOLDER_REPORT_RECIPIENT</code> (falls back to <code className="text-[12px] text-zinc-700">NOTIFICATION_EMAIL</code>).
          </p>
        </div>
        <button
          type="button"
          onClick={runNow}
          disabled={running}
          className="ek-btn ek-btn-primary text-[12.5px] disabled:opacity-50"
          data-testid="stakeholder-report-run-now"
        >
          <Send size={13} /> {running ? "Building & sending…" : "Run report now"}
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase text-zinc-500">Recent reports</h3>
        <button type="button" onClick={reload} className="text-zinc-500 hover:text-black text-[12px]" data-testid="stakeholder-report-refresh">
          <RefreshCw size={12} className="inline mr-1" /> Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-zinc-500 text-[13px]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center">
          <p className="text-zinc-600 text-[14px]">No reports yet. Click <strong>Run report now</strong> to send the first one.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-zinc-200">
          <table className="min-w-full text-[13px]">
            <thead className="bg-zinc-50 text-left text-zinc-500 text-[11px] uppercase tracking-[0.12em]">
              <tr>
                <th className="p-3">Generated</th>
                <th className="p-3">Window</th>
                <th className="p-3">Total leads</th>
                <th className="p-3">Δ</th>
                <th className="p-3">Per collection</th>
                <th className="p-3">Top sub-brands</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-zinc-100 align-top hover:bg-zinc-50/50" data-testid={`stakeholder-run-${it.id}`}>
                  <td className="p-3 text-zinc-700 whitespace-nowrap">{fmtDate(it.generated_at)}</td>
                  <td className="p-3 text-zinc-500 text-[11.5px] whitespace-nowrap">{fmtDate(it.window_start)} →<br/>{fmtDate(it.window_end)}</td>
                  <td className="p-3 tabular-nums font-semibold text-[15px]">{it.total_this_week}</td>
                  <td className="p-3"><DeltaPill pct={it.delta_pct} lastWeek={it.total_last_week} /></td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1 max-w-[300px]">
                      {Object.entries(it.counts || {}).map(([k, v]) => (
                        <span key={k} className="inline-block bg-[#F4EEFD] text-[#7737F6] text-[10.5px] px-1.5 py-0.5 border border-[#7737F6]/30">
                          {k.replace(/_/g, " ")} · <strong>{v}</strong>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {(it.top_sub_brands || []).map((s) => (
                        <span key={s.slug} className="inline-block bg-[#0B0B0C] text-white text-[10.5px] px-1.5 py-0.5">
                          {s.sub_brand} <strong className="text-[#A179FA]">{s.count}</strong>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`inline-block ${it.sent ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"} text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5`}>
                      {it.sent ? "Sent" : "Skipped"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
